import { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Card,
  CardContent,
  CardProps,
  Icon,
  IconButton,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip
} from '@material-ui/core';
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon
} from '@material-ui/icons';
import { Alert, Autocomplete } from '@material-ui/lab';

import { FetchError } from '../../../common/api/api';
import CardTitle from '../../../common/card/CardTitle';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import { PaginatedList } from '../../../common/PaginatedList.interface';
import { useTimeout } from '../../../common/timeout.hook';
import WrappedTableCell from '../../../common/WrappedTableCell';
import TestRailApiProjectPicker from '../components/TestRailApiProjectPicker';
import TestRailApiSuitePicker from '../components/TestRailApiSuitePicker';
import { useGetTestRailApiTestCases } from '../TestRail.hooks';
import {
  TestRailApiProject,
  TestRailApiSuite,
  TestRailApiTestCase,
  TestRailCase
} from '../TestRail.interface';

const StyledCard = styled(Card)`
  .icon {
    margin-right: 10px;
  }
  .link {
    cursor: pointer;
  }
  .fields {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding-bottom: 0px;
  }
  .actions {
    width: 1px;
    padding-top: 2px;
    padding-bottom: 2px;
    padding-left: 2px;
  }
`;

export interface TestRailAssignCardProps {
  testCaseList: PaginatedList<TestRailCase> | undefined;
  icon?: string;
  title?: React.ReactNode;
  loading?: boolean;
  error?: FetchError;
  disabled?: boolean;
  hideInputs?: boolean;
  page?: number;
  limit?: number;
  order?: string;
  direction?: string;
  className?: string;
  cardProps?: CardProps;
  rowsPerPageOptions?: number[];
  actions?: React.ReactNode;
  setPage?: React.Dispatch<React.SetStateAction<number>>;
  setLimit?: React.Dispatch<React.SetStateAction<number>>;
  setOrder?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setDirection?: React.Dispatch<
    React.SetStateAction<'ASC' | 'DESC' | undefined>
  >;
  onSelect?: (
    testRailProject: TestRailApiProject,
    testRailTestCase: TestRailApiTestCase
  ) => void;
  onRemove?: (testCase: TestRailCase) => void;
}

export const TestRailAssignCard: React.FC<TestRailAssignCardProps> = props => {
  const {
    testCaseList,
    icon = 'bug_report',
    title = 'Linked TestRail Test Cases',
    loading,
    error,
    disabled,
    hideInputs,
    page = 0,
    limit = 5,
    order,
    direction,
    rowsPerPageOptions = [5, 10, 25],
    actions,
    setPage,
    setLimit,
    setOrder,
    setDirection,
    onSelect,
    onRemove
  } = props;
  const [selectedTestRailProject, setSelectedTestRailProject] =
    useState<TestRailApiProject | null>(null);
  const [selectedTestRailSuite, setSelectedTestRailSuite] =
    useState<TestRailApiSuite | null>(null);
  const [selectedTestRailTestCase, setSelectedTestRailTestCase] =
    useState<TestRailApiTestCase | null>(null);
  const [removeTestCase, setRemoveTestCase] = useState<TestRailCase>();
  const [debounceSearch, clearDebouncedSearch] = useTimeout();
  const {
    testCaseList: testRailTestCaseList,
    isLoading: isFindingTestCases,
    error: getTestCaseError,
    getTestCases,
    abortGetTestCases,
    setState: setTestCaseState
  } = useGetTestRailApiTestCases();

  const classes = ['test-rail-assign-card'];
  props.className && classes.push(props.className);
  props.cardProps?.className && classes.push(props.cardProps.className);

  const handleSort = (column: string) => {
    setOrder?.(column);
    // default to ASC
    setDirection?.(direction === 'DESC' ? 'ASC' : 'DESC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && direction === 'DESC' ? 'desc' : 'asc';
  };

  const clearTestCaseRequest = () => {
    // clear previous test case fetch request and results
    clearDebouncedSearch();
    abortGetTestCases();
    setTestCaseState({ isLoading: false });
  };

  // remove selected test case on select to re-render field
  useEffect(() => {
    selectedTestRailTestCase && setSelectedTestRailTestCase(null);
  }, [selectedTestRailTestCase]);

  return (
    <StyledCard {...props.cardProps} className={classes.join(' ')}>
      <CardTitle>
        <Icon className='icon'>{icon}</Icon>
        {title}
      </CardTitle>
      {!hideInputs && (
        <CardContent className='fields'>
          <TestRailApiProjectPicker
            project={selectedTestRailProject}
            textFieldProps={{ size: 'small' }}
            onChange={value => {
              clearTestCaseRequest();
              setSelectedTestRailProject(value);
              setSelectedTestRailSuite(null);
              setSelectedTestRailTestCase(null);
            }}
          />
          <TestRailApiSuitePicker
            suite={selectedTestRailSuite}
            disabled={!selectedTestRailProject}
            testRailProjectId={selectedTestRailProject?.id}
            textFieldProps={{ size: 'small' }}
            onChange={value => {
              clearTestCaseRequest();
              setSelectedTestRailSuite(value);
              setSelectedTestRailTestCase(null);
            }}
          />
          <Autocomplete
            fullWidth
            clearOnBlur
            value={selectedTestRailTestCase}
            options={testRailTestCaseList?.cases || []}
            loading={isFindingTestCases}
            // selected suite is not always required
            disabled={disabled || !selectedTestRailProject}
            // test case prefix: C123
            getOptionLabel={testCase => `C${testCase.id}: ${testCase.title}`}
            getOptionSelected={(option, value) => option.id === value.id}
            getOptionDisabled={option => {
              // disable already selected option
              return !!(
                testCaseList &&
                selectedTestRailProject &&
                testCaseList.data.some(testCase => {
                  return (
                    testCase.testRailId === option.id &&
                    // ignore project id check if it's not loaded
                    (!testCase.project ||
                      testCase.project.testRailId ===
                        selectedTestRailProject.id)
                  );
                })
              );
            }}
            onOpen={() => {
              // initial fetch on open
              if (
                selectedTestRailProject &&
                (!testRailTestCaseList ||
                  testRailTestCaseList.cases.length === 0) &&
                !isFindingTestCases
              ) {
                getTestCases({
                  projectId: selectedTestRailProject.id,
                  suiteId: selectedTestRailSuite?.id,
                  limit: 20
                });
              }
            }}
            onInputChange={(_, value, reason) => {
              // only handle searching by user input changes
              // since this callback is fired when selecting/clearing the field
              if (reason !== 'input') {
                return;
              }

              // abort existing fetch request
              clearDebouncedSearch();
              abortGetTestCases();

              // clear state and list since loading may take some time
              // and autocomplete performs its own options filter before loading finishes
              setTestCaseState({ isLoading: true });

              // fetch text cases with filter
              if (selectedTestRailProject) {
                debounceSearch(() => {
                  getTestCases({
                    projectId: selectedTestRailProject.id,
                    suiteId: selectedTestRailSuite?.id,
                    filter: value,
                    limit: 20
                  });
                }, 300);
              }
            }}
            onChange={(_, value) => {
              // clear any errors when selection changes
              setTestCaseState(state => ({ ...state, error: undefined }));

              setSelectedTestRailTestCase(value);
              if (selectedTestRailProject && value) {
                onSelect?.(selectedTestRailProject, value);
              }
            }}
            renderInput={params => (
              <TextField
                {...params}
                fullWidth
                size='small'
                variant='outlined'
                label='Test Case'
                placeholder='Select a Test Case to link'
                error={!!getTestCaseError}
                helperText={getTestCaseError?.message}
              />
            )}
          />
        </CardContent>
      )}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={order === 'testRailId'}
                  direction={getSortDirection('testRailId')}
                  onClick={() => handleSort('testRailId')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'title'}
                  direction={getSortDirection('title')}
                  onClick={() => handleSort('title')}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={3} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {error && (
              <TableRow>
                <TableCell colSpan={3} padding='none'>
                  <Alert severity='error'>{error.message}</Alert>
                </TableCell>
              </TableRow>
            )}
            {(!testCaseList || testCaseList.data.length === 0) && (
              <TableRow>
                <TableCell colSpan={3}>
                  {loading ? (
                    'Loading...'
                  ) : page <= 0 ? (
                    'No linked test cases.'
                  ) : (
                    // for cases where the only item is deleted from the last page,
                    // show a clickable link to go back to the first page
                    // instead of the default empty message
                    <>
                      Go back to{' '}
                      <Link className='link' onClick={() => setPage?.(0)}>
                        page 1
                      </Link>
                      .
                    </>
                  )}
                </TableCell>
              </TableRow>
            )}
            {testCaseList?.data.map((testCase, index) => (
              <TableRow key={`${testCase.id}-${index}`}>
                <TableCell>C{testCase.data.id}</TableCell>
                <WrappedTableCell>{testCase.data.title}</WrappedTableCell>
                <TableCell className='actions'>
                  {testCase.url && (
                    <Tooltip arrow placement='top' title='View Test Case'>
                      <IconButton
                        component={Link}
                        size='small'
                        target='_blank'
                        href={testCase.url}
                      >
                        <OpenInNewIcon color='action' fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip arrow placement='top' title='Unlink Test Case'>
                    <IconButton
                      size='small'
                      onClick={() => setRemoveTestCase(testCase)}
                    >
                      <CloseIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component='div'
        page={page}
        rowsPerPage={limit}
        count={testCaseList?.total || 0}
        rowsPerPageOptions={rowsPerPageOptions}
        onPageChange={(_, page) => setPage?.(page)}
        onRowsPerPageChange={event => {
          setLimit?.(+event.target.value);
          setPage?.(0);
        }}
      />

      {/* render card actions separately */}
      {actions}

      <ConfirmDialog
        open={!!removeTestCase}
        confirmLabel='Unlink'
        title='Unlink Test Case'
        message={`Are you sure you want to unlink ${
          removeTestCase
            ? `the test case "C${removeTestCase.testRailId}: ${removeTestCase.data.title}"`
            : 'this test case'
        }?`}
        onCancel={() => setRemoveTestCase(undefined)}
        onConfirm={() => {
          setRemoveTestCase(undefined);
          removeTestCase && onRemove?.(removeTestCase);
        }}
      />
    </StyledCard>
  );
};
